import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { KanbanSquare, List, Plus, RefreshCw } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Button, Chip, DataTable, EmptyState, PageHeaderBand, Select, ViewToggle } from '@/components/ui'
import { parseDateOnly } from '@/lib/utils/date-only'
import { odooUrl } from '@/lib/utils/odoo-url'
import { useHasAnyPermission } from '@/shell/module-system/permissions'
import { FreightPipelineKanban } from '../components/FreightPipelineKanban'
import { NewQuoteDrawer } from '../components/NewQuoteDrawer'
import { RegisterShipmentDrawer } from '../components/RegisterShipmentDrawer'
import {
  useFreightHealth,
  useFreightPipelineList,
  useFreightShipments,
  useTransitionPipeline,
  type ShipmentStatusFilter,
} from '../api/queries'
import type {
  FreightProviderHealth,
  FreightShipment,
  FreightStatus,
  PipelineStage,
} from '../api/types'

type ViewMode = 'table' | 'board'

const VIEW_OPTIONS = [
  { value: 'table' as const, label: 'Table', icon: List },
  { value: 'board' as const, label: 'Board', icon: KanbanSquare },
]

const STATUS_FILTER_OPTIONS: { value: ShipmentStatusFilter | ''; label: string }[] = [
  { value: '', label: 'Active' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'exception', label: 'Exception' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All statuses' },
]

/**
 * SPI Freight — unified live table of inbound freight across every
 * configured provider.
 *
 * Source: `GET /api/freight/shipments` returns one row per non-terminal
 * freight_shipments record, provider-agnostic. Rows populate via tender
 * writeback, the Odoo [Refresh] button, and the tracking worker beat.
 * Historical rows from retired carriers remain visible via the provider
 * filter; only registered providers get a health chip.
 */
export function LogisticsPage() {
  const [view, setView] = useState<ViewMode>('table')
  const [providerFilter, setProviderFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<ShipmentStatusFilter | ''>('')
  const { data: health } = useFreightHealth()
  const {
    data: shipments,
    isLoading,
    isFetching,
    refetch,
  } = useFreightShipments(providerFilter || undefined, statusFilter || undefined)
  const pipeline = useFreightPipelineList()
  const transition = useTransitionPipeline()
  const navigate = useNavigate()
  const canCreate = useHasAnyPermission(['admin', 'operations', 'operations_logistics', 'purchasing'])
  const [newQuoteOpen, setNewQuoteOpen] = useState(false)
  const [registerOpen, setRegisterOpen] = useState(false)

  const columns = useMemo(() => buildColumns(), [])
  const rows = shipments ?? []
  const pipelineRows = pipeline.data ?? []

  function openPipeline(pipelineId: number) {
    navigate({ to: '/logistics/$pipelineId', params: { pipelineId: String(pipelineId) } })
  }

  const providerOptions = useMemo(() => {
    const fromHealth = (health ?? []).map((h) => ({
      key: h.provider,
      label: h.display_name,
    }))
    // Surface providers present in data but not registered (historical).
    const known = new Set(fromHealth.map((p) => p.key))
    for (const row of rows) {
      if (!known.has(row.provider)) {
        known.add(row.provider)
        fromHealth.push({
          key: row.provider,
          label: row.provider_display_name,
        })
      }
    }
    return fromHealth
  }, [health, rows])

  function transitionPipelineCard(pipelineId: number, target: PipelineStage) {
    transition.mutate(
      { pipelineId: String(pipelineId), targetStage: target },
      {
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'transition failed'
          toast.error(msg, { duration: 5000 })
        },
      },
    )
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-canvas">
      <PageHeaderBand
        title="SPI Freight"
        description="Inbound freight across all carriers — tendered, in transit, out for delivery."
      >
        <div className="ml-auto flex items-center gap-3">
          {(health ?? []).map((h) => (
            <ProviderHealthBadge key={h.provider} health={h} />
          ))}
          <Select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            aria-label="Filter by provider"
          >
            <option value="">All providers</option>
            {providerOptions.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </Select>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ShipmentStatusFilter | '')}
            aria-label="Filter by status"
          >
            {STATUS_FILTER_OPTIONS.map((o) => (
              <option key={o.value || 'active'} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => (view === 'table' ? refetch() : pipeline.refetch())}
            disabled={view === 'table' ? isFetching : pipeline.isFetching}
            title="Refresh"
          >
            <RefreshCw
              size={14}
              className={(view === 'table' ? isFetching : pipeline.isFetching) ? 'animate-spin' : ''}
            />
          </Button>
          <ViewToggle<ViewMode>
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
            aria-label="View mode"
          />
          {canCreate ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => setRegisterOpen(true)}>
                Register shipment
              </Button>
              <Button variant="primary" size="sm" onClick={() => setNewQuoteOpen(true)}>
                <Plus size={14} className="mr-1.5" />
                New quote
              </Button>
            </>
          ) : null}
        </div>
      </PageHeaderBand>

      <div className="flex-1 overflow-hidden">
        {view === 'table' ? (
          isLoading ? (
            <div className="flex h-full items-center justify-center text-secondary">
              Loading shipments…
            </div>
          ) : rows.length === 0 ? (
            <EmptyTrackedShipments filtered={!!statusFilter || !!providerFilter} />
          ) : (
            <div className="h-full overflow-auto">
              <DataTable
                data={rows}
                columns={columns}
                moduleId="logistics"
                getRowId={(row) => String(row.id)}
              />
            </div>
          )
        ) : pipeline.isLoading ? (
          <div className="flex h-full items-center justify-center text-secondary">
            Loading pipeline…
          </div>
        ) : pipelineRows.length === 0 ? (
          <EmptyPipelineBoard />
        ) : (
          <div className="h-full overflow-auto p-4">
            <FreightPipelineKanban
              items={pipelineRows}
              onSelect={(row) => openPipeline(row.id)}
              onTransition={(row, target) =>
                transitionPipelineCard(row.id, target)
              }
            />
          </div>
        )}
      </div>

      <NewQuoteDrawer
        open={newQuoteOpen}
        onOpenChange={setNewQuoteOpen}
        onCreated={openPipeline}
      />
      <RegisterShipmentDrawer
        open={registerOpen}
        onOpenChange={setRegisterOpen}
      />
    </div>
  )
}

function EmptyTrackedShipments({ filtered }: { filtered: boolean }) {
  if (filtered) {
    return (
      <div className="flex h-full items-center justify-center px-6 py-8">
        <EmptyState
          title="No shipments match this filter"
          description="Try a different provider or status — the shipment you're looking for may be terminal (delivered/cancelled) or booked under a different provider."
        />
      </div>
    )
  }
  return (
    <div className="flex h-full items-center justify-center px-6 py-8">
      <EmptyState
        title="No active shipments"
        description={
          <>
            Click <strong>New quote</strong> above to start a freight pipeline for an
            open PO, or request one from a PO row in Procurement&apos;s Open POs
            report. Shipments appear here once a load is tendered.
          </>
        }
      />
    </div>
  )
}

function EmptyPipelineBoard() {
  return (
    <div className="flex h-full items-center justify-center px-6 py-8">
      <EmptyState
        title="No pipeline cards yet"
        description={
          <>
            Click <strong>New quote</strong> above to start one, or request a quote
            from a PO row in Procurement&apos;s Open POs report. Cards also appear
            automatically for loads already tendered elsewhere (Odoo, CARLA).
          </>
        }
      />
    </div>
  )
}

function buildColumns(): ColumnDef<FreightShipment>[] {
  return [
    {
      accessorKey: 'odoo_po_name',
      header: 'Odoo PO',
      size: 110,
      cell: ({ getValue, row }) => (
        <a
          href={odooUrl('purchase.order', row.original.odoo_po_id)}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-accent-text hover:underline"
        >
          {getValue<string>()}
        </a>
      ),
    },
    {
      accessorKey: 'provider_display_name',
      header: 'Provider',
      size: 150,
    },
    {
      accessorKey: 'provider_ref',
      header: 'Reference',
      size: 130,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 140,
      cell: ({ getValue, row }) => (
        <span>
          <StatusChip status={getValue<FreightStatus>()} />
          {row.original.delivered_elsewhere ? (
            <span
              className="block text-warning-text text-xs mt-1"
              title="Inbound shipments should end at the Arlington dock. Check for a drop-ship, a wrong tracking number on the PO, or a misdelivery."
            >
              Not delivered to Arlington
            </span>
          ) : null}
        </span>
      ),
    },
    {
      accessorKey: 'carrier_name',
      header: 'Carrier',
      size: 180,
      cell: ({ getValue, row }) => {
        const name = getValue<string | null>()
        const scac = row.original.carrier_scac
        if (!name && !scac) return <span className="text-tertiary">—</span>
        return (
          <span>
            {name ?? '—'}
            {scac ? <span className="text-tertiary ml-1.5">({scac})</span> : null}
          </span>
        )
      },
    },
    {
      accessorKey: 'last_location',
      header: 'Last Location',
      size: 200,
      cell: ({ getValue }) => (
        <span>{getValue<string | null>() ?? <span className="text-tertiary">—</span>}</span>
      ),
    },
    {
      id: 'pickup',
      header: 'Pickup',
      size: 110,
      cell: ({ row }) => {
        const { pickup_date, first_event_at, status } = row.original
        if (pickup_date) return formatDate(pickup_date)
        // Use first tracking event as a pickup proxy for in-motion statuses
        // when TQL hasn't returned a formal pickup date (TRAX-tendered loads).
        if (first_event_at && ['in_transit', 'out_for_delivery', 'delivered'].includes(status)) {
          return (
            <span title="Approximate — derived from first tracking event">
              ~{formatInstantAsDate(first_event_at)}
            </span>
          )
        }
        return <span className="text-tertiary">—</span>
      },
    },
    {
      accessorKey: 'est_delivery',
      header: 'Est. Delivery',
      size: 130,
      cell: ({ getValue }) => formatDate(getValue<string | null>()),
    },
    {
      accessorKey: 'actual_delivery',
      header: 'Actual Delivery',
      size: 130,
      cell: ({ getValue }) => formatDate(getValue<string | null>()),
    },
    {
      accessorKey: 'last_check_at',
      header: 'Tracking',
      size: 190,
      cell: ({ row }) => <TrackingHealth shipment={row.original} />,
    },
  ]
}

/** Whether the row is actually being tracked. A row that only ever failed
 * used to show a fresh "Last Synced" time next to a stale "Tendered". */
function TrackingHealth({ shipment }: { shipment: FreightShipment }) {
  const { tracked, track_failures, last_error, last_success_at } = shipment
  if (!tracked) {
    return (
      <span className="text-tertiary" title="Manual entry. No carrier is polled for this shipment.">
        Manual, not tracked
      </span>
    )
  }
  if (track_failures > 0) {
    return (
      <span title={last_error ?? undefined}>
        <span className="text-danger-text">
          Failing ({track_failures}): {shortError(last_error)}
        </span>
        <span className="block text-tertiary text-xs">
          Last good: {last_success_at ? formatDateTime(last_success_at) : 'never'}
        </span>
      </span>
    )
  }
  if (!last_success_at) return <span className="text-tertiary">Not checked yet</span>
  return <>{formatDateTime(last_success_at)}</>
}

function shortError(err: string | null): string {
  if (!err) return 'unknown error'
  if (/over 30 days old/i.test(err)) return 'carrier says too old to track'
  if (/not authori[sz]ed|forbidden|\b403\b/i.test(err)) return 'not on our account'
  if (/\b404\b|no record/i.test(err)) return 'carrier has no record'
  if (/\b401\b|credentials/i.test(err)) return 'carrier login rejected'
  if (/\b400\b|validation/i.test(err)) return 'carrier rejected the reference'
  return err.length > 40 ? `${err.slice(0, 40)}…` : err
}

/** pickup_date / est_delivery / actual_delivery are date-only
 * ("YYYY-MM-DD") — parseDateOnly avoids the classic
 * new Date(dateOnlyString) UTC-midnight shift that displays the prior
 * calendar day in any US timezone. NOT for real timestamps — see
 * `formatInstantAsDate` below for `first_event_at`. */
function formatDate(iso: string | null): React.ReactNode {
  if (!iso) return <span className="text-tertiary">—</span>
  try {
    return parseDateOnly(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

/** `first_event_at` is a real ISO timestamp (earliest carrier tracking
 * event, `services/freight/router.py::_first_event_at`) — a genuine
 * instant, not a bare calendar date, so `new Date(iso)` is the correct
 * parse here (unlike `formatDate` above, `parseDateOnly` would break on
 * a full timestamp's extra `-`-separated offset component). */
function formatInstantAsDate(iso: string | null): React.ReactNode {
  if (!iso) return <span className="text-tertiary">—</span>
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

function formatDateTime(iso: string | null): React.ReactNode {
  if (!iso) return <span className="text-tertiary">—</span>
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function StatusChip({ status }: { status: FreightStatus }) {
  const label = STATUS_LABEL[status] ?? status
  const variant = STATUS_VARIANT[status] ?? 'neutral'
  return (
    <Chip size="xs" variant={variant}>
      {label}
    </Chip>
  )
}

const STATUS_LABEL: Record<FreightStatus, string> = {
  unknown: 'Unknown',
  needs_scheduling: 'Needs Scheduling',
  scheduled: 'Scheduled',
  tendered: 'Tendered',
  dispatched: 'Dispatched',
  in_transit: 'In Transit',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  exception: 'Exception',
  cancelled: 'Cancelled',
}

const STATUS_VARIANT: Record<
  FreightStatus,
  'success' | 'info' | 'warning' | 'danger' | 'neutral'
> = {
  unknown: 'neutral',
  needs_scheduling: 'info',
  scheduled: 'info',
  tendered: 'info',
  dispatched: 'info',
  in_transit: 'warning',
  out_for_delivery: 'warning',
  delivered: 'success',
  exception: 'danger',
  cancelled: 'danger',
}

function ProviderHealthBadge({ health }: { health: FreightProviderHealth }) {
  const state = !health.enabled
    ? 'disabled'
    : health.mode === 'live' && health.reachable === false
      ? 'error'
      : health.mode
  const label = `${health.display_name}: ${state}`
  const variant =
    state === 'live'
      ? 'success'
      : state === 'error'
        ? 'danger'
        : state === 'dormant'
          ? 'warning'
          : 'neutral'
  return (
    <Chip size="xs" variant={variant} dot>
      {label}
    </Chip>
  )
}
